// src/components/Countdown.tsx
//
// RFA Phase 4 §4.1 — the app's first client-side timer. Display-only: the
// server's expiresAt timestamps are the source of truth; this just re-renders
// mm:ss from `to - Date.now()` on a 250ms interval, clamping at 0.
import { useEffect, useState } from 'react';

/** Formats a millisecond duration as mm:ss, clamped at 00:00. */
export function formatClock(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const mm = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
  const ss = String(totalSeconds % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

export function Countdown({
  to,
  label,
  expiredText,
}: {
  to: number;
  label: string;
  /** Shown in place of 00:00 once the clock runs out (soft-clock hint). */
  expiredText?: string;
}) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, []);

  const remaining = to - now;
  return (
    <span className="inline-flex items-baseline gap-1 text-sm">
      <span className="text-gray-600">{label}</span>
      {remaining <= 0 && expiredText !== undefined ? (
        <span className="font-semibold text-amber-700">{expiredText}</span>
      ) : (
        <span className="font-mono text-lg font-semibold tabular-nums">
          {formatClock(remaining)}
        </span>
      )}
    </span>
  );
}
